import * as React from 'react';
import { useEffect, useState } from 'react';
import JsxParser from './JsxParser';
import '../css/table.sass';
import CampfireButton from './CampfireButton';
import StaticElementButton from './StaticElementButton';

type Theme = 'night' | 'day';
export interface CampfireTableProps {
  id: string;
  title: string;
  columnHeadings: string[];
  columnClassNames?: string[];
  mobileColumnIndices: number[];
  rows: React.ReactNode[][];
  components?: any;
  initialNumOfRows?: number;
  moreButtonLabel?: string;
  fewerButtonLabel?: string;
  rowTheme?: Theme[];
  focusableTabIndex?: boolean;
  expandButtonLabel?: string;
  expandButtonName?: string;
}

interface CampfireTableState {
  displayAllContents: boolean;
  mobileViewDetails: boolean[];
  mobileRestColumnIndex: number[];
}

export default function CampfireTable(props: CampfireTableProps) {
  const {
    initialNumOfRows = 3,
    moreButtonLabel = 'See More Data',
    fewerButtonLabel = 'See Fewer Data',
    expandButtonLabel = 'expand-bill',
    expandButtonName = 'expand',
    rowTheme = new Array(props.rows.length).fill('day'),
    focusableTabIndex = true
  } = props;

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const listener = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', listener);

    return () => window.removeEventListener('resize', listener);
  }, []);

  const handleFocus = (displayAllContents: boolean) => {
    const lastItem = displayAllContents
      ? document.getElementById(`tr-${props.id}-${initialNumOfRows}`)
      : document.getElementById(`tr-${props.id}-0`);

    if (lastItem) {
      lastItem.focus();
    }
  };

  const [state, setState] = React.useState<CampfireTableState>({
    displayAllContents: false,
    mobileViewDetails: [],
    mobileRestColumnIndex: []
  });

  const loadAllData = () => {
    setState({
      ...state,
      displayAllContents: !state.displayAllContents
    });
    handleFocus(state.displayAllContents);
  };

  let keyIndex = 0;

  const mobileOtherIndices: number[] = [];
  for (let i = 0; i < props.columnHeadings.length; i++) {
    if (props.mobileColumnIndices.indexOf(i) === -1) {
      mobileOtherIndices.push(i);
    }
  }

  const headings = props.columnHeadings.map(
    (heading: string, index: number) => {
      let className = 'campfire-th d-none d-md-table-cell';
      props.mobileColumnIndices.forEach((mobileColumnIndex: number) => {
        if (mobileColumnIndex === index) {
          className = 'campfire-th';
        }
      });
      keyIndex += 1;
      return (
        <th
          key={`${heading.toLowerCase().replace(' ', '')}${keyIndex}`}
          className={`${className}${
            props.columnClassNames &&
            props.columnClassNames[index] !== undefined
              ? ` ${props.columnClassNames[index]}`
              : ''
          }${props.columnHeadings.length - 1 === index ? ' lastCol' : ''}`}
        >
          {heading}
        </th>
      );
    }
  );

  const tableContents = state.displayAllContents
    ? props.rows
    : props.rows.slice(0, initialNumOfRows);

  const rows = tableContents.map((row: React.ReactNode[], rowIndex: number) => {
    keyIndex += 1;
    return (
      <React.Fragment
        key={`${row.toString().toLowerCase().replace(' ', '')}${keyIndex}`}
      >
        <tr
          tabIndex={focusableTabIndex ? 0 : -1}
          id={`tr-${props.id}-${rowIndex}`}
          className={`tableRow ${
            rowTheme[rowIndex] ? rowTheme[rowIndex] : 'day'
          } ${
            state.mobileViewDetails[rowIndex]
              ? 'campfire-tr tr-expanded '
              : 'campfire-tr '
          }`}
        >
          {row.map((cell: React.ReactNode, colIndex: number) => {
            let className = 'campfire-td d-none d-md-table-cell';
            let iconClassName =
              'campfire-td d-none d-md-none d-lg-none d-xl-none expand-icon';

            props.mobileColumnIndices.forEach((mobileColumnIndex: number) => {
              if (mobileColumnIndex === colIndex) {
                className = 'campfire-td strong';
                if (
                  props.mobileColumnIndices[
                    props.mobileColumnIndices.length - 1
                  ] === colIndex &&
                  props.columnHeadings.length !==
                    props.mobileColumnIndices.length
                ) {
                  iconClassName =
                    'campfire-td d-md-none d-lg-none d-xl-none expand-icon';
                }
              }
            });

            keyIndex += 1;
            return (
              <React.Fragment key={`cell${colIndex.toString()}${keyIndex}`}>
                <td
                  className={`${className}${
                    props.columnClassNames &&
                    props.columnClassNames[colIndex] !== undefined
                      ? ` ${props.columnClassNames[colIndex]}`
                      : ''
                  }${
                    props.columnHeadings.length - 1 === colIndex
                      ? ' lastCol'
                      : ''
                  }`}
                >
                  {typeof cell === 'string' ? (
                    <JsxParser jsx={cell} components={props.components} />
                  ) : (
                    cell
                  )}
                </td>
                {windowWidth < 992 &&
                  colIndex ===
                    props.mobileColumnIndices[
                      props.mobileColumnIndices.length - 1
                    ] && (
                    <td className={iconClassName} style={{ width: '65px' }}>
                      <StaticElementButton
                        name={expandButtonName}
                        id={`tr-expand-${rowIndex}`}
                        label={expandButtonLabel}
                        className="glyphicon glyphicon-plus"
                        type="span"
                        onClick={() => {
                          const newDetailsState: boolean[] = [
                            ...state.mobileViewDetails
                          ];
                          newDetailsState[rowIndex] =
                            !newDetailsState[rowIndex];
                          setState({
                            ...state,
                            mobileViewDetails: newDetailsState,
                            mobileRestColumnIndex: mobileOtherIndices
                          });
                        }}
                      >
                        {state.mobileViewDetails[rowIndex] ? (
                          <>&#8722;</>
                        ) : (
                          <>&#43;</>
                        )}
                      </StaticElementButton>
                    </td>
                  )}
              </React.Fragment>
            );
          })}
        </tr>

        {state.mobileViewDetails[rowIndex] && (
          <tr
            className={`d-md-none d-lg-none d-xl-none campfire-tr tr-expandedPanel ${
              rowTheme[rowIndex] ? rowTheme[rowIndex] : 'day'
            }`}
          >
            <td colSpan={props.columnHeadings.length}>
              <div className="expandedPanel">
                {state.mobileRestColumnIndex.map((value) => {
                  keyIndex += 1;
                  return (
                    <React.Fragment
                      key={`restColumn${value.toString()}${keyIndex}`}
                    >
                      <div className="heading">
                        {props.columnHeadings[value]}
                      </div>
                      <div className="value">
                        {typeof row[value] === 'string' ? (
                          <JsxParser
                            jsx={row[value]}
                            components={props.components}
                          />
                        ) : (
                          row[value]
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      <table id={props.id} className="campfire-table">
        <thead className="campfire-thead">
          <tr className="campfire-tr">{headings}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>

      <div className="alignLoadDataBtn">
        {props.rows.length > initialNumOfRows && (
          <CampfireButton
            name="loadAllData"
            id={`loadAllData-${props.id}`}
            onClick={loadAllData}
            label={
              state.displayAllContents ? fewerButtonLabel : moreButtonLabel
            }
            color="water"
            ghost
            smol
          />
        )}
      </div>
    </>
  );
}
